import { TOPBTN, TOPBTN_WARN } from "#puf/constants.ts";
import { validateMachine } from "#puf/machines/validate.ts";
import { store } from "#puf/store/index.ts";
import { collectStateIds } from "@pixelpuf/schemas/pxpf";
import { useSelector } from "@xstate/store-react";
import { useState } from "react";

/**
 * Top strip of the machine editor: structural-edit buttons + validation trigger.
 * Buttons that act on a selected state/transition disable when nothing matches.
 *
 * "Add State" auto-names the new state `state_N` where N is the next free
 * integer suffix. "Add Transition" pushes a placeholder transition whose
 * source/target/event fields can then be edited via the properties panel.
 */
export function EditorToolbar() {
  const stateMachine = useSelector(store, (s) => s.context.stateMachine);
  const selectedStateId = useSelector(store, (s) => s.context.selectedStateId);
  const selectedTransitionIndex = useSelector(store, (s) => s.context.selectedTransitionIndex);

  const [validateMessage, setValidateMessage] = useState<string | null>(null);

  const hasSelection = selectedStateId !== null || selectedTransitionIndex !== null;

  function nextStateId(): string {
    if (!stateMachine) return "state_1";
    const ids = collectStateIds(stateMachine);
    let n = 1;
    while (ids.has(`state_${n}`)) n++;
    return `state_${n}`;
  }

  function onAddState() {
    const id = nextStateId();
    // Auto-place new node at viewport-ish center (xyflow viewport queries are
    // expensive at toolbar level; this is good enough — user can drag).
    store.trigger["state.add"]({ id, position: { x: 200, y: 200 } });
    store.trigger["editor.selectState"]({ id });
  }

  function onAddTransition() {
    if (!stateMachine) return;
    const ids = [...collectStateIds(stateMachine)];
    const from = selectedStateId && ids.includes(selectedStateId) ? selectedStateId : ids[0];
    const to = ids[0];
    if (!from || !to) return;
    const transition = { from, to, event: "EVENT", trigger: "manual" as const };
    const index = stateMachine.transitions.length;
    store.trigger["transition.add"]({ transition });
    store.trigger["editor.selectTransition"]({ index });
  }

  function onSetInitial() {
    if (!selectedStateId) return;
    store.trigger["state.setInitial"]({ id: selectedStateId });
  }

  function onDeleteSelected() {
    if (selectedStateId) {
      store.trigger["state.delete"]({ id: selectedStateId });
      return;
    }
    if (selectedTransitionIndex !== null) {
      store.trigger["transition.delete"]({ index: selectedTransitionIndex });
    }
  }

  function onValidate() {
    if (!stateMachine) {
      setValidateMessage("no machine yet — add a state first");
      return;
    }
    const report = validateMachine(stateMachine);
    if (report.issues.length === 0) {
      setValidateMessage("✓ no issues");
    } else {
      const lines = report.issues.map((i) => `${i.severity.toUpperCase()}: ${i.message}`);
      setValidateMessage(lines.join("\n"));
    }
  }

  return (
    <div className="flex flex-col gap-2 px-3 py-2 border-b border-line bg-bg-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={onAddState} className={TOPBTN}>+ State</button>
        <button onClick={onAddTransition} disabled={!stateMachine} className={TOPBTN}>+ Transition</button>
        <button onClick={onSetInitial} disabled={!selectedStateId} className={TOPBTN}>Set initial</button>
        <button onClick={onDeleteSelected} disabled={!hasSelection} className={TOPBTN_WARN}>Delete</button>
        <span className="flex-1" />
        <button onClick={onValidate} className={TOPBTN}>Validate</button>
      </div>
      {validateMessage && (
        <pre className="text-[11px] text-dim whitespace-pre-wrap font-mono bg-bg-3 border border-line rounded-sm p-2 max-h-32 overflow-auto">
          {validateMessage}
        </pre>
      )}
    </div>
  );
}
